import {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSpinner,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    Alert,
    TablePagination,
} from "@mui/material";
import axios from "axios";
import { getAppSettings, baseUrl, requestID, config } from "../../../../common/utils"; // 1. Added requestID import

/* ================= TYPES ================= */

interface ApiPayment {
    id: number;
    amount: string;
    quantity: string;
    payment_id: string;
    invoice_id: string;
    vendor_id: string;
    status: string;
    created_at: string;
    updated_at: string;
}

interface ApiResponse {
    total: number;
    data: ApiPayment[];
}

interface PaymentRow {
    id: number;
    amount: number;
    quantity: number;
    paymentRef: string;
    invoiceRef: string;
    status: string;
    date: string;
}

/* ================= COMPONENT ================= */

const PaymentHistoryDashboard = () => {
    const { siteName } = getAppSettings();

    // Data State
    const [payments, setPayments] = useState<PaymentRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    /* ================= FETCH DATA (AXIOS POST) ================= */

    useEffect(() => {
        fetchPayments();
    }, [page, rowsPerPage]);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            setError(null);

            const offset = page * rowsPerPage;

            // 2. Create FormData
            const formData = new FormData();
            formData.append('limit', String(rowsPerPage));
            formData.append('offset', String(offset));
            formData.append('requestID', requestID());
            

            // 3. Use Axios POST
            const response = await axios.post<ApiResponse>(
                `${baseUrl()}Geacloud_Invoices/get_payments_history`,
                formData,config
            );

          //  console.log(response);
            const json = response.data;

            setTotalCount(json.total || 0);

            const apiData = json.data || [];
            const mappedPayments: PaymentRow[] = apiData.map((item) => ({
                id: item.id,
                amount: parseFloat(item.amount),
                quantity: parseInt(item.quantity, 10),
                paymentRef: item.payment_id,
                invoiceRef: item.invoice_id,
                status: item.status,
                date: item.created_at,
            }));

            setPayments(mappedPayments);

        } catch (err: any) {
            console.error(err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch payment history";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /* ================= HELPERS ================= */

    const getStatusConfig = (status: string) => {
        const s = status.toLowerCase();
        if (s === "success" || s === "paid") return { color: "success", label: "Successful" };
        if (s === "pending") return { color: "warning", label: "Pending" };
        if (s === "cancelled" || s === "failed") return { color: "error", label: "Failed" };
        return { color: "default", label: status };
    };

    // --- Pagination Handlers ---
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /* ================= RENDER ================= */

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>Payments</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Payment History
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Paper
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: "rgba(15,23,42,0.85)", // Dark theme base
                            backdropFilter: "blur(6px)",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {loading && payments.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                                <IonSpinner name="crescent" />
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {["Amount / Ref", "Quantity", "Status", "Date"].map((header) => (
                                                    <TableCell
                                                        key={header}
                                                        sx={{
                                                            backgroundColor: "rgba(2,6,23,0.95)",
                                                            color: "#cbd5f5",
                                                            fontWeight: 600,
                                                            fontSize: "0.75rem",
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.08em",
                                                        }}
                                                        align={header === "Quantity" ? "center" : "left"}
                                                    >
                                                        {header}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {payments.length === 0 && !loading ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                                                        No payment records found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                payments.map((row) => {
                                                    const statusConfig = getStatusConfig(row.status);
                                                    return (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            sx={{
                                                                transition: "background-color 0.2s ease",
                                                                "&:hover": { backgroundColor: "rgba(148,163,184,0.08)" },
                                                            }}
                                                        >
                                                            {/* COLUMN 1: Amount & Payment Ref */}
                                                            <TableCell sx={{ color: "#e5e7eb" }}>
                                                                <Typography fontWeight={600} fontSize="1rem">
                                                                    ₦{row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: "#94a3b8", display: 'block', mt: 0.5 }}>
                                                                    {row.paymentRef}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* COLUMN 2: Quantity */}
                                                            <TableCell align="center">
                                                                <Chip
                                                                    label={row.quantity}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{
                                                                        color: "#cbd5f5",
                                                                        borderColor: "rgba(203, 213, 245, 0.3)",
                                                                        fontWeight: 600,
                                                                        minWidth: "40px"
                                                                    }}
                                                                />
                                                            </TableCell>

                                                            {/* COLUMN 3: Status */}
                                                            <TableCell>
                                                                <Chip
                                                                    label={statusConfig.label}
                                                                    color={statusConfig.color as any}
                                                                    size="small"
                                                                    sx={{ fontWeight: 500, borderRadius: "6px" }}
                                                                />
                                                            </TableCell>

                                                            {/* COLUMN 4: Date */}
                                                            <TableCell sx={{ color: "#e5e7eb" }}>
                                                                {new Date(row.date).toLocaleDateString()}
                                                                <Typography variant="caption" display="block" sx={{ color: "#94a3b8" }}>
                                                                    {new Date(row.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    sx={{
                                        color: "#cbd5f5",
                                        borderTop: "1px solid rgba(148,163,184,0.2)",
                                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                            marginTop: "0",
                                            marginBottom: "0"
                                        },
                                        ".MuiTablePagination-selectIcon": {
                                            color: "#cbd5f5"
                                        }
                                    }}
                                />
                            </>
                        )}
                    </Paper>
                </Box>
            </IonContent>
        </IonPage>
    );
};

export default PaymentHistoryDashboard;